import { MouseEventHandler, ReactNode } from 'react'

import styled, { css } from 'styled-components'
import {
  color,
  ColorProps,
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  margin,
  MarginProps,
  padding,
  PaddingProps,
  width,
  zIndex,
  ZIndexProps,
} from 'styled-system'
import { themeGet } from '@styled-system/theme-get'

import { DIV_SHOULD_FORWARD_PROP } from 'Config/shouldForwardProp'

import { gap, GapProps } from 'Services/Styles'

type AsProps = {
  as?: 'ol' | 'ul' | 'nav' | 'a'
}

type ElementProps = LayoutProps &
  MarginProps &
  PaddingProps &
  ColorProps &
  ZIndexProps &
  Omit<FlexboxProps, 'flexDirection'> &
  GapProps &
  AsProps & {
    alignCenter?: boolean
    alignEnd?: boolean
    alignStart?: boolean
    borderBottom?: boolean
    borderTop?: boolean
    children?: ReactNode
    className?: string
    fullHeight?: boolean
    fullScreen?: boolean
    fullWidth?: boolean
    fullwidth?: boolean
    justifyCenter?: boolean
    justifyEnd?: boolean
    noShrink?: boolean
    spaceBetween?: boolean
    squeeze?: boolean
    grow?: boolean
    wrap?: boolean
    relative?: boolean
    pointer?: boolean
    onClick?: MouseEventHandler<HTMLDivElement>
  }

const alignCenterCss = ({ alignCenter }: ElementProps) =>
  alignCenter &&
  css`
    align-items: center;
  `

const alignEndCss = ({ alignEnd }: ElementProps) =>
  alignEnd &&
  css`
    align-items: flex-end;
  `

const alignStartCss = ({ alignStart }: ElementProps) =>
  alignStart &&
  css`
    align-items: flex-start;
  `

const borderBottomCss = ({ borderBottom }: ElementProps) =>
  borderBottom &&
  css`
    border-bottom: solid 1px ${themeGet('colors.divider.default')};
  `

const borderTopCss = ({ borderTop }: ElementProps) =>
  borderTop &&
  css`
    border-top: solid 1px ${themeGet('colors.divider.default')};
  `

const fullHeightCss = ({ fullHeight }: ElementProps) =>
  fullHeight &&
  css`
    height: 100%;
  `

const fullScreenCss = ({ fullScreen }: ElementProps) =>
  fullScreen &&
  css`
    width: 100vw;
    height: 100vh;
  `

const fullWidthCss = ({ fullWidth }: ElementProps) =>
  fullWidth &&
  css`
    width: 100%;
  `

// legacy
const fullwidthCss = ({ fullwidth }: ElementProps) =>
  fullwidth &&
  css`
    width: 100%;
  `
// ----

const justifyCenterCss = ({ justifyCenter }: ElementProps) =>
  justifyCenter &&
  css`
    justify-content: center;
  `

const justifyEndCss = ({ justifyEnd }: ElementProps) =>
  justifyEnd &&
  css`
    justify-content: flex-end;
  `

const noShrinkCss = ({ noShrink }: ElementProps) =>
  noShrink &&
  css`
    flex-shrink: 0;
  `

const spaceBetweenCss = ({ spaceBetween }: ElementProps) =>
  spaceBetween &&
  css`
    justify-content: space-between;
  `

const squeezeCss = ({ squeeze }: ElementProps) =>
  squeeze &&
  css`
    width: fit-content;
  `

const growCss = ({ grow }: ElementProps) =>
  grow &&
  css`
    flex-grow: 1;
  `

const wrapCss = ({ wrap }: ElementProps) =>
  wrap &&
  css`
    flex-wrap: wrap;
  `

const relativeCss = ({ relative }: ElementProps) =>
  relative &&
  css`
    position: relative;
  `

const pointerCss = ({ pointer }: ElementProps) =>
  pointer &&
  css`
    cursor: pointer;
  `

const shouldForwardProp = (prop: string | number) => {
  return !DIV_SHOULD_FORWARD_PROP.includes(String(prop))
}

export const Element = styled.div.withConfig({
  shouldForwardProp,
})<ElementProps>`
  ${pointerCss}

  ${layout}
  ${margin}
  ${padding}
  ${color}
  ${flexbox}
  ${gap}
  ${width}

  ${alignCenterCss}
  ${alignEndCss}
  ${alignStartCss}
  ${borderBottomCss}
  ${borderTopCss}
  ${fullHeightCss}
  ${fullWidthCss}
  ${justifyCenterCss}
  ${justifyEndCss}
  ${noShrinkCss}
  ${spaceBetweenCss}
  ${squeezeCss}
  ${fullScreenCss}
  ${wrapCss}
  ${growCss}
  ${relativeCss}
  ${zIndex}
`

export const Column = styled(Element)<ElementProps>`
  display: flex;
  flex-direction: column;
`

export const Row = styled(Element)<ElementProps>`
  display: flex;
`

// legacy
export default styled.div<ElementProps>`
  display: flex;
  box-sizing: border-box;

  ${fullwidthCss}

  ${flexbox}
  ${margin}
  ${padding}
  ${layout}
`
